import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ExternalLink,
  FileText,
  FolderOpen,
  ImageIcon,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import {
  RESOURCE_KINDS,
  RESOURCE_KIND_LABELS,
  RESOURCE_KIND_MIME_TYPES,
  RESOURCE_ROUTES,
  isUploadableResourceKind,
  resourceAdminListResponseSchema,
  resourceInputSchema,
  resourceDeletedResponseSchema,
  resourceUpdateSchema,
  resourceResponseSchema,
  type Resource,
  type ResourceAdminQuery,
  type ResourceFileInput,
  type ResourceInput,
  type ResourceKind,
  type ResourceUpdate,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, Modal, SkeletonTable } from "@/shared/ui/ui";
import {
  formatFileSize,
  readResourceCover,
  readResourceFile,
} from "@/features/resources/resources.api";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";

const NO_PREFIX = { baseUrl: "" } as const;

interface ResourcePage {
  items: Resource[];
  total: number;
  page: number;
  perPage: number;
}

/** Filtros y paginación EN LA URL: el servidor devuelve solo la página. */
function listResources(query: ResourceAdminQuery): Promise<ResourcePage> {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.query) params.set("query", query.query);
  if (query.kind) params.set("kind", query.kind);
  if (query.status) params.set("status", query.status);
  return authFetch(
    `${RESOURCE_ROUTES.adminList}?${params.toString()}`,
    resourceAdminListResponseSchema,
    NO_PREFIX,
  );
}

async function createResource(input: ResourceInput): Promise<Resource> {
  const data = await authFetch(RESOURCE_ROUTES.adminCreate, resourceResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.resource;
}

async function updateResource(id: string, update: ResourceUpdate): Promise<Resource> {
  const data = await authFetch(RESOURCE_ROUTES.adminOne(id), resourceResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body: update,
  });
  return data.resource;
}

async function deleteResource(id: string): Promise<void> {
  await authFetch(RESOURCE_ROUTES.adminOne(id), resourceDeletedResponseSchema, {
    ...NO_PREFIX,
    method: "DELETE",
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Recursos — réplica de `admin/v2/resources` (Anahí, 27-ago): la
 * biblioteca de formación para socios — videos, PDFs, imágenes y
 * enlaces. El alta va en modal, como Sedes y Cupones; un recurso se
 * APAGA, no se borra (los socios pueden tenerlo guardado).
 *
 * El formulario pide SOLO lo del tipo elegido (Anahí, 28-ago): el tipo
 * es el PRIMER campo porque decide todo lo de abajo. PDF e imagen se
 * SUBEN; video y enlace van por URL, y el formulario lo dice —esconder
 * el botón sin explicar por qué solo genera la siguiente pregunta.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminResourcesPage() {
  return (
    <AdminGuard>
      <ResourcesScreen />
    </AdminGuard>
  );
}

const EMPTY_FORM = {
  title: "",
  description: "",
  url: "",
  kind: "pdf" as ResourceKind,
  imageUrl: "",
};

/** Qué se le pide a cada tipo, en una línea, dentro del formulario. */
const KIND_HINTS: Record<ResourceKind, string> = {
  video:
    "Los videos van por enlace (YouTube, Vimeo o Drive): uno solo pesa más que toda la biblioteca junta, y esas plataformas ya lo reproducen por tramos.",
  pdf: "Hasta 8 MB. Se guarda en el sistema y el socio lo descarga desde su biblioteca.",
  imagen: "Hasta 8 MB. La imagen es también la portada de su tarjeta.",
  link: "La dirección a la que lleva la tarjeta.",
};


function ResourcesScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [status, setStatus] = useState("");

  const query: ResourceAdminQuery = {
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
    ...(kindFilter ? { kind: kindFilter as ResourceKind } : {}),
    ...(status ? { status: status as "active" | "inactive" } : {}),
  };
  const resources = useQuery({
    queryKey: ["admin", "resources", query],
    queryFn: () => listResources(query),
    placeholderData: (previous) => previous,
  });

  const [open, setOpen] = useState(false);
  /*
   * ── CRUD completo (Anahí, 29-ago) ──────────────────────────────────
   * El mismo formulario da de alta y EDITA: `editando` guarda el id de
   * lo que se está tocando, o `null` cuando es un alta. Un segundo modal
   * para editar tendría los mismos ocho campos y las mismas reglas de
   * tipo, y sería el que se olvide de actualizar la próxima vez.
   */
  const [editando, setEditando] = useState<Resource | null>(null);
  /* A qué recurso se le preguntó "¿seguro?". Un `confirm()` del
   * navegador bloquea la pestaña entera; esto se resuelve en la fila. */
  const [porBorrar, setPorBorrar] = useState<Resource | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState<(ResourceFileInput & { sizeBytes: number }) | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const crear = useMutation({
    mutationFn: createResource,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "resources"] });
      setOpen(false);
    },
    onError: (cause) =>
      setFormError(cause instanceof Error ? cause.message : t("No se pudo crear el recurso")),
  });

  const actualizar = useMutation({
    mutationFn: ({ id, update }: { id: string; update: ResourceUpdate }) =>
      updateResource(id, update),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "resources"] }),
    onError: (cause) =>
      setFormError(cause instanceof Error ? cause.message : t("No se pudo guardar el recurso")),
  });

  const borrar = useMutation({
    mutationFn: deleteResource,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "resources"] });
      setPorBorrar(null);
    },
  });

  function abrirAlta() {
    setEditando(null);
    setForm(EMPTY_FORM);
    setFile(null);
    setFormError(null);
    setOpen(true);
  }

  /**
   * Editar: el formulario abre con lo que ya tiene.
   *
   * El ARCHIVO no se precarga —son megabytes que no viajan en el
   * listado— y por eso no hace falta reenviarlo para guardar: sin
   * archivo nuevo, el recurso conserva el que tenía. Sí se enseña cuál
   * es, para que nadie crea que lo perdió.
   */
  function abrirEdicion(resource: Resource) {
    setEditando(resource);
    setForm({
      title: resource.title,
      description: resource.description ?? "",
      url: resource.url ?? "",
      kind: resource.kind,
      imageUrl: resource.imageUrl ?? "",
    });
    setFile(null);
    setFormError(null);
    setOpen(true);
  }

  /** Cambiar de tipo tira lo que ya no aplica: el enlace del video no es
   * el PDF, y arrastrarlo en silencio manda al servidor un recurso con
   * dos fuentes que el contrato rebota. */
  function cambiarTipo(kind: ResourceKind) {
    setFormError(null);
    setFile(null);
    // Una IMAGEN es su propia portada: la que se hubiera subido sobra.
    setForm({ ...form, kind, url: "", ...(kind === "imagen" ? { imageUrl: "" } : {}) });
  }

  async function elegirArchivo(chosen: File | undefined) {
    if (!chosen) return;
    setFormError(null);
    try {
      const leido = await readResourceFile(chosen, form.kind);
      setFile({ ...leido, sizeBytes: chosen.size });
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : t("No se pudo leer el archivo"));
    }
  }

  async function elegirPortada(chosen: File | undefined) {
    if (!chosen) return;
    setFormError(null);
    try {
      setForm({ ...form, imageUrl: await readResourceCover(chosen) });
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : t("No se pudo leer la portada"));
    }
  }

  function guardar(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const sube = isUploadableResourceKind(form.kind);

    /*
     * ── EDITAR: el archivo es opcional ─────────────────────────────
     * Al alta hay que subirlo; editando, no: si no se elige otro, el
     * recurso conserva el suyo. Por eso se valida con el esquema
     * PARCIAL, que no lo exige, en vez de obligar a resubir 5 MB para
     * corregir una tilde del título.
     */
    if (editando) {
      const cambios = resourceUpdateSchema.safeParse({
        title: form.title,
        description: form.description.trim() || undefined,
        ...(sube
          ? file
            ? { file: { fileName: file.fileName, mimeType: file.mimeType, dataBase64: file.dataBase64 } }
            : {}
          : { url: form.url }),
        kind: form.kind,
        ...(form.kind !== "imagen" && form.imageUrl ? { imageUrl: form.imageUrl } : {}),
      });
      if (!cambios.success) {
        setFormError(cambios.error.issues[0]?.message ?? t("Revisa los datos del recurso"));
        return;
      }
      actualizar.mutate(
        { id: editando.id, update: cambios.data },
        { onSuccess: () => setOpen(false) },
      );
      return;
    }

    const parsed = resourceInputSchema.safeParse({
      title: form.title,
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
      // Cada tipo con su ÚNICA fuente: mandar las dos es lo que el
      // contrato rechaza, y aquí es donde se decide cuál toca.
      ...(sube
        ? file
          ? { file: { fileName: file.fileName, mimeType: file.mimeType, dataBase64: file.dataBase64 } }
          : {}
        : { url: form.url }),
      kind: form.kind,
      ...(form.imageUrl && form.kind !== "imagen" ? { imageUrl: form.imageUrl } : {}),
      active: true,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t("Revisa los datos del recurso"));
      return;
    }
    crear.mutate(parsed.data);
  }

  const items = resources.data?.items ?? [];
  const total = resources.data?.total ?? 0;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Recursos")}
        description={t("La biblioteca de formación para socios: videos, PDFs y enlaces.")}
      />

      <DataTableFrame aria-busy={resources.isFetching}>
        {/* El alta vive DENTRO de la tabla, arriba a la derecha (Anahí). */}
        <FilterBar
          actions={
            <Button leftIcon={<Plus />} onClick={abrirAlta}>
              {t("Nuevo recurso")}
            </Button>
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="relative w-56">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Título…")}
                className="pl-10"
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Tipo")}
            <div className="w-36">
              <Select
                aria-label={t("Tipo")}
                value={kindFilter}
                onChange={(event) => {
                  setKindFilter(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={RESOURCE_KINDS.map((value) => ({
                  value,
                  label: t(RESOURCE_KIND_LABELS[value]),
                }))}
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Estado")}
            <div className="w-36">
              <Select
                aria-label={t("Estado")}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={[
                  { value: "active", label: t("Activos") },
                  { value: "inactive", label: t("Apagados") },
                ]}
              />
            </div>
          </label>
        </FilterBar>
        {resources.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={4} columns={5} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<FolderOpen className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía no hay recursos")}
              description={t("Sube el primero: los socios lo verán en su panel.")}
              action={
                <Button size="sm" leftIcon={<Plus />} onClick={abrirAlta}>
                  {t("Nuevo recurso")}
                </Button>
              }
            />
          </div>
        ) : (
          <DataTable caption={t("Recursos de formación")}>
            <thead>
              <tr>
                <Th>{t("Recurso")}</Th>
                <Th>{t("Tipo")}</Th>
                <Th>{t("Material")}</Th>
                <Th>{t("Estado")}</Th>
                <Th align="right">{t("Acciones")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((resource) => (
                <Tr key={resource.id}>
                  <Td>
                    <p className="font-medium">{resource.title}</p>
                    {resource.description && (
                      <p className="max-w-[420px] truncate text-[12px] text-ds-ink-3">
                        {resource.description}
                      </p>
                    )}
                  </Td>
                  <Td>
                    <Badge variant="info" size="sm">
                      {t(RESOURCE_KIND_LABELS[resource.kind])}
                    </Badge>
                  </Td>
                  {/* El material es un ARCHIVO guardado o un enlace: nunca
                      los dos, así que la columna enseña el que hay. */}
                  <Td>
                    {resource.file ? (
                      <span className="inline-flex max-w-[260px] items-center gap-1.5 text-[13px] text-ds-ink-2">
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{resource.file.fileName}</span>
                        <span className="shrink-0 text-ds-ink-3">
                          · {formatFileSize(resource.file.sizeBytes)}
                        </span>
                      </span>
                    ) : resource.url ? (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex max-w-[260px] items-center gap-1.5 truncate text-[13px] font-medium text-ds-violet-pop hover:underline"
                      >
                        <span className="truncate">{resource.url}</span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-[13px] text-ds-ink-3">{t("Sin material")}</span>
                    )}
                  </Td>
                  <Td>
                    <Badge variant={resource.active ? "success" : "neutral"} size="sm" dot>
                      {resource.active ? t("Activo") : t("Apagado")}
                    </Badge>
                  </Td>
                  <Td align="right">
                    {/*
                      ── Editar · apagar · eliminar (Anahí, 29-ago) ─────
                      "Debe ser un CRUD, donde editar, eliminar, crear
                      también." Apagar y eliminar NO son lo mismo y por
                      eso conviven: apagado sale de la biblioteca y se
                      puede reencender; eliminado se va con todo y sus
                      megabytes, para lo que no debió subirse.

                      La confirmación se resuelve en la propia fila, no
                      con un `confirm()` del navegador: ese bloquea la
                      pestaña entera mientras está abierto.
                    */}
                    {porBorrar?.id === resource.id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-[12.5px] text-ds-ink-3">{t("¿Eliminar?")}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-ds-danger"
                          loading={borrar.isPending}
                          onClick={() => borrar.mutate(resource.id)}
                        >
                          {t("Sí, eliminar")}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setPorBorrar(null)}>
                          {t("Cancelar")}
                        </Button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Pencil />}
                          onClick={() => abrirEdicion(resource)}
                        >
                          {t("Editar")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={
                            actualizar.isPending && actualizar.variables?.id === resource.id
                          }
                          onClick={() =>
                            actualizar.mutate({
                              id: resource.id,
                              update: { active: !resource.active },
                            })
                          }
                        >
                          {resource.active ? t("Apagar") : t("Encender")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t("Eliminar")}
                          className="text-ds-ink-3 hover:text-ds-danger"
                          onClick={() => setPorBorrar(resource)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editando ? t("Editar recurso") : t("Nuevo recurso")}
      >
        <form onSubmit={guardar} className="grid gap-4">
          <Input
            label={t("Título")}
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            placeholder={t("Cómo compartir tu enlace de invitación")}
            maxLength={100}
          />
          <Input
            label={t("Descripción (opcional)")}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            maxLength={300}
          />

          {/*
           * ── Tipo y material, UNA fila (Anahí, 28-ago) ──────────────
           * El tipo manda y su material va al lado, no cuatro campos más
           * abajo: se elige "PDF" y el botón de subirlo está ahí mismo.
           * En móvil se apila, que dos controles en 360 px no caben.
           */}
          <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
            <Select
              label={t("Tipo")}
              value={form.kind}
              onChange={(event) => cambiarTipo(event.target.value as ResourceKind)}
              options={RESOURCE_KINDS.map((value) => ({
                value,
                label: t(RESOURCE_KIND_LABELS[value]),
              }))}
            />
            {/* La fuente: SOLO la que corresponde al tipo elegido. */}
            {isUploadableResourceKind(form.kind) ? (
              <div className="grid gap-1.5">
                <span className="text-[13px] font-medium text-ds-ink-2">
                  {form.kind === "pdf" ? t("El PDF") : t("La imagen")}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={RESOURCE_KIND_MIME_TYPES[form.kind].join(",")}
                  className="hidden"
                  aria-label={form.kind === "pdf" ? t("Subir PDF") : t("Subir imagen")}
                  onChange={(event) => {
                    void elegirArchivo(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    type="button"
                    leftIcon={<Upload />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {file
                      ? t("Cambiar archivo")
                      : form.kind === "pdf"
                        ? t("Subir PDF")
                        : t("Subir imagen")}
                  </Button>
                  {file ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-[13px] text-ds-ink-3">
                      <FileText className="h-4 w-4 shrink-0" />
                      <span className="truncate">{file.fileName}</span>
                      <span className="shrink-0">· {formatFileSize(file.sizeBytes)}</span>
                    </span>
                  ) : (
                    /*
                      Editando, el archivo que YA tiene: sus bytes no
                      viajan en el listado, así que no se puede precargar
                      —ni hace falta—. Enseñar cuál es evita que alguien
                      lo vuelva a subir creyendo que lo perdió.
                    */
                    editando?.file && (
                      <span className="inline-flex min-w-0 items-center gap-1.5 text-[13px] text-ds-ink-3">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate">{editando.file.fileName}</span>
                        <span className="shrink-0">
                          · {formatFileSize(editando.file.sizeBytes)} · {t("ya guardado")}
                        </span>
                      </span>
                    )
                  )}
                </div>
                <p className="text-[12px] text-ds-ink-3">{t(KIND_HINTS[form.kind])}</p>
              </div>
            ) : (
              <Input
                label={form.kind === "video" ? t("Enlace del video") : t("URL del enlace")}
                value={form.url}
                onChange={(event) => setForm({ ...form, url: event.target.value })}
                placeholder="https://…"
                maxLength={500}
                hint={t(KIND_HINTS[form.kind])}
              />
            )}
          </div>

          {/* La PORTADA se sube (Anahí, 28-ago: fuera la URL). Una imagen
              no la pide: la portada es ella misma. */}
          {form.kind !== "imagen" && (
            <div className="grid gap-1.5">
              <span className="text-[13px] font-medium text-ds-ink-2">
                {t("Portada de la tarjeta (opcional)")}
              </span>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                aria-label={t("Subir portada")}
                onChange={(event) => {
                  void elegirPortada(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <div className="flex flex-wrap items-center gap-3">
                {form.imageUrl && (
                  <img
                    src={form.imageUrl}
                    alt=""
                    className="h-14 w-20 rounded-xl object-cover shadow-ds-float"
                  />
                )}
                <Button
                  variant="secondary"
                  type="button"
                  leftIcon={<ImageIcon />}
                  onClick={() => coverInputRef.current?.click()}
                >
                  {form.imageUrl ? t("Cambiar portada") : t("Subir portada")}
                </Button>
              </div>
              <p className="text-[12px] text-ds-ink-3">
                {t("Sin portada, la biblioteca pinta el icono del tipo.")}
              </p>
            </div>
          )}

          {formError && (
            <Alert variant="error" onDismiss={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
              {t("Cancelar")}
            </Button>
            <Button type="submit" loading={crear.isPending || actualizar.isPending}>
              {editando ? t("Guardar cambios") : t("Crear recurso")}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
